import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { CopyButton } from '@/_shared/components/CopyButton';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { codeBlockCustomStyle, SyntaxHighlighter } from '@/_shared/lib/syntax';

import { CLICKHOUSE_TEARDOWN_STEPS } from '../../lib/clickhouse-setup';
import { refreshPermissionOnRefusal, useWarehousePermission } from './accountCreatePermission';
import { PermissionNotice } from './PermissionNotice';
import { refusalMessage } from './refusalCopy';
import { RemovalImpactSummary } from './RemovalImpactSummary';
import { OutputSection } from './shared/OutputSection';
import { COUNTING_IMPACT, removalImpactLoading, useRemovalImpact } from './useRemovalImpact';
import type { WarehouseAccount } from './useWarehouseAccounts';

/** One Reverse ETL source that blocks the revoke, with the RudderStack route to it. */
interface InUseSource {
  id: string;
  url: string;
}

/**
 * The blocking sources the `account_in_use` CONFLICT carries.
 *
 * Duck-typed like `DeleteAccountDialog`'s `conflictDetailsFrom`: the client error is not always a
 * real `ORPCError` instance (cross-realm `instanceof` is not guaranteed), so the JSON shape is read
 * directly. An older server, or any other refusal, simply yields none and the message stands alone.
 */
function inUseSourcesFrom(err: unknown): InUseSource[] {
  if (!err || typeof err !== 'object') return [];
  const data = (err as { data?: { inUseSources?: unknown } }).data;
  if (!Array.isArray(data?.inUseSources)) return [];
  return data.inUseSources.filter(
    (s): s is InUseSource =>
      !!s &&
      typeof s === 'object' &&
      typeof (s as InUseSource).id === 'string' &&
      typeof (s as InUseSource).url === 'string',
  );
}

export function RevokeAccountDialog({
  account,
  onOpenChange,
}: {
  account: WarehouseAccount | null;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const syntaxStyle = useSyntaxStyle();
  // A refused revoke refreshes the shared permission; the open dialog follows it.
  const permission = useWarehousePermission();
  const revoke = useMutation(
    orpc.settings.warehouses.revokeAccount.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() }),
      onError: (err) => refreshPermissionOnRefusal(queryClient, orpc, err),
    }),
  );
  const result = revoke.data;
  // The confirm waits until the dialog shows what uses the account.
  const impact = useRemovalImpact(account && !revoke.data ? account.id : null);
  const inUseSources = inUseSourcesFrom(revoke.error);
  // RudderStack refused the delete itself (ACT2-1276). No probe can say when that changes, so Revoke
  // stays disabled for this account until the dialog is opened again.
  const deleteForbidden =
    (revoke.error as { data?: { reason?: unknown } } | null)?.data?.reason === 'rudderstack_delete_forbidden';
  // The server deletes the account whether or not the dialog is still open; closing mid-request
  // would lose the teardown it answers. Every close path waits for the request.
  const pending = revoke.isPending;
  // The dialog stays mounted between accounts, so every close path must clear the last result.
  const close = () => {
    revoke.reset();
    onOpenChange(false);
  };
  return (
    <Dialog
      open={!!account}
      onOpenChange={(open) => {
        if (open) onOpenChange(true);
        else if (!pending) close();
      }}
    >
      <DialogContent
        closeDisabled={pending}
        onEscapeKeyDown={(e) => {
          if (pending) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (pending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{result ? 'Account revoked' : 'Revoke account in RudderStack'}</DialogTitle>
          {!result && (
            <DialogDescription>
              This deletes the RudderStack account for <span className="font-medium">{account?.name}</span> and the
              credential RudderStack holds for it. The connection stays in Lookout marked “Reconnect required”. Delete
              the connection afterwards if you no longer need it.
            </DialogDescription>
          )}
        </DialogHeader>
        {!result && account && <RemovalImpactSummary accountId={account.id} path="revoke" />}
        {revoke.isError && (
          <Callout variant="error">
            <p>{refusalMessage(revoke.error, 'Could not revoke the account')}</p>
            {/*
              "Remove them from the account in RudderStack" is only actionable if the admin can find
              them. config-backend names the blocking sources, so each one is a link rather than an
              id to go hunting for.
            */}
            {inUseSources.length > 0 && (
              <>
                <p className="mt-2 text-xs font-semibold tracking-wide uppercase">Sources using this account</p>
                <ul className="list-disc pl-4">
                  {inUseSources.map((s) => (
                    <li key={s.id} className="text-xs">
                      <a className="underline" href={s.url} target="_blank" rel="noopener noreferrer">
                        {s.id}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Callout>
        )}
        {/*
          The account really is gone in RudderStack — the teardown below is still the SQL to run — but
          Lookout could not mark this connection for reconnect, so it still looks live and offers no
          Reconnect. Said here because nothing else would say it: the card shows a normal row.
        */}
        {result?.paused === false && (
          <Callout variant="warning">
            The account was deleted in RudderStack, but this connection could not be marked “Reconnect required” here.
            Run the teardown below, then revoke again to finish marking it — it will report the account as already gone.
          </Callout>
        )}
        {result?.teardown.kind === 'sql' && account?.type.toUpperCase() === 'CLICKHOUSE' && (
          <Callout variant="info">
            <p className="font-medium">Finish in ClickHouse, in this order</p>
            <ol className="list-decimal pl-4">
              {CLICKHOUSE_TEARDOWN_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </Callout>
        )}
        {result?.teardown.kind === 'sql' && (
          <OutputSection
            label="Finish in your warehouse"
            action={<CopyButton text={result.teardown.sql} label="Copy" copyLabel="Teardown SQL" />}
          >
            <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
              {result.teardown.sql}
            </SyntaxHighlighter>
          </OutputSection>
        )}
        {result?.teardown.kind === 'instructions' && (
          <Callout variant="info">
            {result.teardownWithheld === 'shared_snowflake_user' && (
              <p className="font-medium">The Snowflake user was kept</p>
            )}
            <ol className="list-decimal pl-4">
              {result.teardown.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ol>
          </Callout>
        )}
        {!result && <PermissionNotice permission={permission} />}
        <DialogFooter>
          {result ? (
            <Button type="button" variant="secondary" onClick={close} label="Done" />
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={close} disabled={pending} label="Cancel" />
              <ActionTooltip show={removalImpactLoading(impact)} message={COUNTING_IMPACT}>
                <Button
                  type="button"
                  variant="primary-destructive"
                  loading={revoke.isPending}
                  disabled={permission.blocked !== null || deleteForbidden || removalImpactLoading(impact)}
                  onClick={() => revoke.mutate({ connectionId: account!.id })}
                  label="Revoke account"
                />
              </ActionTooltip>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
